<script lang="ts">
  import { cn } from '@/lib/utils';
  import Switch from '@/lib/components/ui/switch/switch.svelte';
  import Am5Loader from '@/Util/Am5Loader.svelte';
  import { parseStorage, saveStorage } from '@/Util/utils';
  import type { ActivityMarker, Breakthrough, RaceEvent } from './chartData';
  import type { MatchedDay, MeasureRow } from './progressionApi';
  import ProgressionChart from './ProgressionChart.svelte';
  import ProgressionForecast from './ProgressionForecast.svelte';
  import SignatureCharts from './SignatureCharts.svelte';
  import { chartGroups, type ChartGroup, type HistoryGroup } from './seriesConfig';

  export let rows: MeasureRow[] | null = null;
  export let irParams: any = null;
  export let breakthroughs: Breakthrough[] | null = null;
  /** Activities tagged Race or Event, drawn among the breakthroughs. */
  export let raceEvents: RaceEvent[] | null = null;
  /** Flagged activities and ones whose signature or training load was set by hand. */
  export let markers: ActivityMarker[] | null = null;
  export let measuresFailed: boolean = false;
  /** The applied date range, as `YYYY-MM-DD` in the athlete's timezone; `from` is empty for "all". */
  export let from: string = '';
  export let to: string = '';
  export let today: string = '';
  /** Where an XFAI plan starts (or will start), `YYYY-MM-DD` in the athlete's timezone; '' when there is none. */
  export let programStart: string = '';
  /** While a filter is on: the XSS of the activities that matched, day by day (the rest is greyed out). */
  export let matchedDays: MatchedDay[] | null = null;

  const STORAGE_KEY = 'progressionCharts';
  const saved = parseStorage(STORAGE_KEY);
  let group: ChartGroup = chartGroups.some((g) => g.value === saved.group) ? saved.group : 'XPMC';
  let hideFuture: boolean = saved.hideFuture === true;

  // Arrow keys move between the chart tabs, as they do in any tab list, taking focus with them.
  function onTabKeydown(event: KeyboardEvent) {
    const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
    if (!step) return;
    event.preventDefault();
    const index = chartGroups.findIndex((option) => option.value === group);
    const next = chartGroups[(index + step + chartGroups.length) % chartGroups.length];
    choose(next.value);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const tabs = (event.currentTarget as HTMLElement).querySelectorAll<HTMLElement>('[role=tab]');
    tabs[chartGroups.indexOf(next)]?.focus();
  }

  function choose(next: ChartGroup) {
    group = next;
    saveStorage(STORAGE_KEY, 'group', group);
  }
  function setHideFuture(checked: boolean) {
    hideFuture = checked;
    saveStorage(STORAGE_KEY, 'hideFuture', hideFuture);
  }

  $: historyGroup = group === 'XPMC' || group === 'Stress' ? (group as HistoryGroup) : null;
</script>

<div class="flex flex-col gap-3">
  <div class="flex flex-row flex-wrap items-center justify-between gap-2">
    <div class="flex flex-row flex-wrap gap-1" role="tablist" aria-label="Progression chart" on:keydown={onTabKeydown}>
      {#each chartGroups as option}
        <button
          type="button"
          role="tab"
          aria-selected={group === option.value}
          tabindex={group === option.value ? 0 : -1}
          class={cn(
            'rounded-full border border-solid px-3 py-1 text-sm cursor-pointer',
            group === option.value
              ? 'border-primary bg-primary/10 text-primary font-medium'
              : 'border-border bg-white text-muted-foreground hover:text-foreground'
          )}
          on:click={() => choose(option.value)}
        >
          {option.label}
        </button>
      {/each}
    </div>
    {#if group !== 'Forecast'}
      <label class="inline-flex items-center gap-2 text-sm text-muted-foreground cursor-pointer select-none">
        <Switch checked={hideFuture} onCheckedChange={setHideFuture} />
        Hide Future Activities
      </label>
    {/if}
  </div>

  {#if group === 'Forecast'}
    <ProgressionForecast />
  {:else if measuresFailed}
    <div class="text-sm text-red-600">Couldn't load your fitness history.</div>
  {:else if !rows}
    <div class="w-full h-[300px] md:h-[360px] flex items-center justify-center text-sm text-muted-foreground">Loading...</div>
  {:else}
    <Am5Loader>
      {#if historyGroup}
        <ProgressionChart group={historyGroup} {rows} {irParams} {breakthroughs} {raceEvents} {markers} {from} {to} {today} {programStart} {hideFuture} {matchedDays} on:select />
      {:else}
        <SignatureCharts {rows} {irParams} {breakthroughs} {raceEvents} {markers} {from} {to} {today} {programStart} {hideFuture} on:select />
      {/if}
    </Am5Loader>
  {/if}
</div>
